import { Button, Divider, Icon, Text, useModal } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useRetryBillIntegrationSyncMutation } from '@features/attachments/hooks/useRetryBillSync'
import { CegidIntegrationStatus } from '@features/cegidIntegration/enums/cegidIntegrationStatus'
import { useDeleteBillAction } from '@views/bills/elements/BillEditableForm/hooks/useDeleteBillAction'
import { DeleteBillConfirmationModal } from '@views/bills/elements/DeleteBillConfirmationModal'
import { useBillIntegrationStatusMessageKey } from '@views/bills/hooks/useBillIntegrationStatusKey'
import { getLatestIntegrationStatus } from '@views/bills/utils/getLatestIntegrationStatus'
import { isBillDeletable } from '@views/bills/utils/isBillDeletable'
import { isBillSyncRetryable } from '@views/bills/utils/isBillSyncRetryable'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'
import { getReceiptTransferStatusPreviewMessageKey } from '@views/receipts/utils/getReceiptTransferStatusPreviewMessageKey'

import { ModalId } from '../../../../../../../../../src/enums/modalId'
import { QueryKeys } from '../../../../../../../../../src/enums/queryKeys'
import { useSegment } from '../../../../../../../../../src/hooks'
import { useAttachmentPreviewModal } from '../../../../context/attachmentPreviewModalContext'
import * as Styled from './styles'

export const ReceiptSyncStatusSection = (): ReactElement => {
  const { integrationStatuses, bill, close: closePreviewModal } = useAttachmentPreviewModal()
  const { t } = useTranslation()
  const { track } = useSegment()
  const titleMessageKey = getReceiptTransferStatusPreviewMessageKey(integrationStatuses)

  const integrationStatus = getLatestIntegrationStatus(integrationStatuses)
  const messageKey = useBillIntegrationStatusMessageKey(integrationStatus)

  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal(
    ModalId.BillDeleteConfirmationModal,
  )

  const queryClient = useQueryClient()

  // Toasts are already included in this hook
  const { deleteBill: deleteBillAPI, isDeleting } = useDeleteBillAction({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      closeDeleteConfirmationModal()
      closePreviewModal()
    },
  })

  const isDeletable = integrationStatus
    ? isBillDeletable({
        bill,
        integrationStatus: [integrationStatus],
      })
    : false

  const isRetryable = integrationStatus
    ? isBillSyncRetryable({
        integrationStatus: [integrationStatus],
      })
    : false

  const deleteBill = () => {
    if (!integrationStatus) {
      return
    }

    track(ExpensesTrackingEvents.BillDeleteFromSimplifiedAttachmentViewClick)

    deleteBillAPI(integrationStatus.billId)
  }

  const { retryIntegration, isLoading } = useRetryBillIntegrationSyncMutation({
    integrationStatus,
  })

  if (!titleMessageKey) {
    return <></>
  }

  return (
    <Styled.ReceiptSyncStatusSectionWrapper>
      <Divider />
      <Text weight="bold">{t('cegid_integration_status.header')}</Text>
      <Styled.StatusRow>
        <Styled.StatusIconWrapper>
          <Icon color="currentColor" icon="refresh" size="sm" />
        </Styled.StatusIconWrapper>
        <Text variant="bodyMD">{t(titleMessageKey)}</Text>
      </Styled.StatusRow>
      {integrationStatus?.status === CegidIntegrationStatus.Failed && messageKey ? (
        <>
          <Text variant="bodyMD">{t(messageKey)}</Text>
          {isDeletable ? (
            <Button disabled={isDeleting} onClick={() => openDeleteConfirmationModal()} variant="filled" color="danger">
              {t('bill.action.delete')}
            </Button>
          ) : null}
          {isRetryable ? (
            <Button disabled={isLoading} onClick={() => retryIntegration()} variant="outlined">
              {t('bill.action.retry_sync')}
            </Button>
          ) : null}
        </>
      ) : null}

      <DeleteBillConfirmationModal
        id={ModalId.BillDeleteConfirmationModal}
        onOk={() => deleteBill()}
        isDeleting={isDeleting}
      />
    </Styled.ReceiptSyncStatusSectionWrapper>
  )
}
